import React from 'react';
import { useSelector } from 'react-redux';
import { createUseStyles } from 'react-jss';
import {
  Button, Pane, TextInput, toaster,
  Table, Strong, Text,
} from 'evergreen-ui';
import * as yup from 'yup';
import FormField from 'Utils/FormField';
import CornerDialogError from 'Utils/CornerDialogError';
import { VirtualList } from 'Utils/VirtualList';
import SuspenseSpinner from 'Utils/SuspenseSpinner';
import { userInfoSelector } from 'Services/auth.service';
import { useVirtualListTableStyles } from 'styles/useStyles';
import { useGetUserRolesQuery, useUpdateUserMutation } from 'Services/api/usersApi';
import { UserPasswordDialog } from './Dialog.UserPassword';

const useStyles = createUseStyles({
  userFormWrapper: {
    display: 'grid',
    padding: '.5em',
    gridTemplateRows: '1fr max-content',
    gap: '.5rem',
    overflow: 'hidden',
  },
  userControlWrapper: {
    display: 'grid',
    gridTemplateRows: 'max-content 1fr',
    gridRowGap: '.5em',
    // overflow: 'auto',
  },
  userControl: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr) max-content',
    gridColumnGap: '.5em',
    alignItems: 'end',
    '& input': {
      width: '100%',
    },
  },
});

const validationForm = yup.object().shape({
  username: yup
    .string()
    .required('Введите имя пользователя'),
  emailID: yup
    .string()
    .required('Введите email'),
});

export default function UserInfoRouter() {
  const classes = useStyles();
  const tableClasses = useVirtualListTableStyles();
  const userinfo = useSelector(userInfoSelector);
  const { currentUser } = userinfo;
  const [localCurrentUser, setLocalCurrentUser] = React.useState(currentUser);
  const [isShownUserPassword, setIsShownUserPassword] = React.useState(false);
  const [, setIsSaving] = React.useState(false);
  const [errors, setErrors] = React.useState([]);

  const { data: userRoles = [], isFetching: isLoadingRoles } = useGetUserRolesQuery(localCurrentUser.username, { skip: !localCurrentUser.username });
  const [updateUser] = useUpdateUserMutation();

  const clearErrors = React.useCallback(() => setErrors([]), []);

  const onChangeUser = React.useCallback((e) => {
    const { name, value } = e.target;
    setLocalCurrentUser((cur) => ({
      ...cur,
      [name]: value,
    }));
  }, []);

  const onChangeUserPhone = React.useCallback((e) => {
    const { name, value } = e.target;
    setLocalCurrentUser((cur) => ({
      ...cur,
      attributes: {
        ...cur?.attributes,
        [name]: value,
      },
    }));
  }, []);

  const saveUser = React.useCallback(async () => {
    try {
      const fetchParams = {
        user: localCurrentUser.username,
        // id: localCurrentUser.subject,
        email: localCurrentUser.emailID,
        username: localCurrentUser.username,
        firstName: localCurrentUser.firstname,
        lastName: localCurrentUser.lastname,
        phone: localCurrentUser.attributes.phone,
      };
      setIsSaving(true);
      await updateUser(fetchParams).unwrap();
      setIsSaving(false);
      toaster.success('Пользователь сохранен!');
    } catch (err) {
      toaster.danger(err.message);
      setIsSaving(false);
    }
  }, [localCurrentUser.attributes.phone, localCurrentUser.emailID, localCurrentUser.firstname, localCurrentUser.lastname, localCurrentUser.username, updateUser]);

  // проверяем ввод имени и почты пользователя
  const submit = React.useCallback(() => {
    clearErrors();
    validationForm.validate(localCurrentUser, { abortEarly: false })
      .then(() => {
        saveUser();
      })
      .catch((err) => setErrors(err.errors));
  }, [clearErrors, saveUser, localCurrentUser]);

  const UserRoleRow = React.useCallback((item) => (
    <Table.Row
      isSelectable
      key={item.id}
      height={64}
    >
      <Table.Cell>
        <Pane
          display="grid"
          gridTemplateRows="max-content max-content"
          gridRowGap=".2em"
          gridColumnGap=".5em"
        >
          <Strong size={400}>{item.name}</Strong>
          <Text size={300} color="muted">{item.description}</Text>
        </Pane>
      </Table.Cell>
    </Table.Row>
  ), []);

  return (
    <>
      <CornerDialogError errors={errors} closeDialog={clearErrors} />
      <UserPasswordDialog
        user={localCurrentUser}
        isShown={isShownUserPassword}
        setIsShown={setIsShownUserPassword}
      />

      <div className={classes.userFormWrapper}>
        <div className={classes.userControlWrapper}>
          <Pane className={classes.userControl}>
            <FormField label="Имя пользователя*">
              <TextInput name="username" disabled value={localCurrentUser.username} />
            </FormField>
            <FormField label="Email*">
              <TextInput name="emailID" isInvalid={!localCurrentUser.emailID} value={localCurrentUser.emailID || ''} onChange={onChangeUser} />
            </FormField>
            <FormField label="Имя">
              <TextInput name="firstname" value={localCurrentUser.firstname || ''} onChange={onChangeUser} />
            </FormField>
            <FormField label="Фамилия">
              <TextInput name="lastname" value={localCurrentUser.lastname || ''} onChange={onChangeUser} />
            </FormField>
            <FormField label="Телефон">
              <TextInput name="phone" value={localCurrentUser?.attributes?.phone || ''} onChange={onChangeUserPhone} />
            </FormField>
            <FormField label="Пароль">
              <div>
                <Button
                  onClick={() => { setIsShownUserPassword(true); }}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Задать пароль
                </Button>
              </div>
            </FormField>
          </Pane>
          <Pane className={tableClasses.tableWrapper}>
            <Table>
              <Table.Head>
                <Table.HeaderCell><Strong>Роли пользователя</Strong></Table.HeaderCell>
              </Table.Head>
            </Table>
            {
              // eslint-disable-next-line no-nested-ternary
              isLoadingRoles
                ? <SuspenseSpinner />
                : !userRoles.length
                  ? <Table.Row><Table.Cell><Text>Выберите роли справа</Text></Table.Cell></Table.Row>
                  : <VirtualList data={userRoles} row={UserRoleRow} />
            }
          </Pane>
        </div>

        <Pane textAlign="right">
          <Button
            onClick={submit}
            appearance="primary"
            intent="success"
          >
            Сохранить
          </Button>
        </Pane>
      </div>
    </>
  );
}
